import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import { Calendar, User, ArrowRight, Search, BookOpen } from 'lucide-react';
import { API_URL } from '../config';
import '../styles/Blog.css';


const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/blog`);
      setPosts(response.data);
    } catch (err) {
      console.error('Error fetching blog posts:', err);
    } finally {
      setLoading(false);
    }
  };

  // Get unique categories
  const categories = ['all', ...new Set(posts.map(p => p.category).filter(Boolean))];

  // Filter posts
  const filteredPosts = posts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.excerpt?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <>
      <Header />
      <div className="blog-page">
        <div className="blog-hero">
          <div className="blog-hero-content">
            <h1>Security Resources</h1>
            <p>Practical tips, guides, and insights to help you stay safe online</p>
          </div>
        </div>

        <div className="blog-container">
          {/* Search and Filter Bar */}
          <div className="blog-filters">
            <div className="blog-search">
              <Search size={18} />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div className="blog-categories">
              {categories.map(cat => (
                <button
                  key={cat}
                  className={`category-btn ${selectedCategory === cat ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(cat)}
                >
                  {cat === 'all' ? 'All Posts' : cat}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="blog-loading">
              <div className="loading-spinner" />
              <p>Loading articles...</p>
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="blog-empty">
              <BookOpen size={48} />
              <h3>No articles found</h3>
              <p>
                {searchTerm || selectedCategory !== 'all'
                  ? 'Try adjusting your search or filter'
                  : 'Check back soon for new security insights'}
              </p>
            </div>
          ) : (
            <div className="blog-grid">
              {filteredPosts.map((post, index) => (
                <Link 
                  to={`/blog/${post.slug}`} 
                  key={post.id} 
                  className={`blog-card ${index === 0 ? 'featured' : ''}`}
                >
                  {post.featured_image && (
                    <div className="blog-card-image">
                      <img src={post.featured_image} alt={post.title} />
                    </div>
                  )}
                  <div className="blog-card-content">
                    {post.category && (
                      <span className="blog-card-category">{post.category}</span>
                    )}
                    <h2 className="blog-card-title">{post.title}</h2>
                    {post.excerpt && (
                      <p className="blog-card-excerpt">{post.excerpt}</p>
                    )}
                    <div className="blog-card-meta">
                      <span className="blog-card-date">
                        <Calendar size={14} />
                        {formatDate(post.published_at)}
                      </span>
                      {post.author && (
                        <span className="blog-card-author">
                          <User size={14} />
                          {post.author}
                        </span>
                      )}
                    </div>
                    <span className="blog-card-link">
                      Read more <ArrowRight size={16} />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Blog;
